import React from 'react';
import styles from './reviewLicenses.module.scss';
import FormModal from 'v2/common/FormModal/FormModal';
import classnames from 'classnames';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { toast } from 'react-toastify';
import {
  User,
  ChargebeeSubscription,
  DjangoListResponse,
  PlanPermission,
} from 'v2/models';
import { Spinner } from 'react-bootstrap';
import LicensesForm, { SubscriptionsChangesField } from './LicensesForm';
import { getTitle } from 'v2/utils/userUtils';
import { infiniteStrapiUsersKeys } from 'v2/queries';

interface ReviewLicensesModalProps {
  onClose?: () => void;
  member: User;
  readOnly?: boolean;
}

const ReviewLicensesModal = ({
  onClose,
  member,
  readOnly = false,
}: ReviewLicensesModalProps) => {
  const queryClient = useQueryClient();
  const formContext = useForm<SubscriptionsChangesField>({
    mode: 'onChange',
    defaultValues: {
      licensesChanges: [],
    },
  });

  const {
    data: ownedSubscriptions,
    isError: ownedSubscriptionsIsError,
    refetch: ownedSubscriptionsRefetch,
  } = useQuery(
    ['Get subcriptions by member', member.id],
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<ChargebeeSubscription>
      >(`/dj/api/v1/chargebee_subscription_multiuser/`, {
        size: 100,
      });
      return response;
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: ownedPlanPermissions,
    isError: ownedPlanPermissionsIsError,
    refetch: ownedPlanPermissionsRefetch,
  } = useQuery(
    ['Get plan permissions by member', member.id],
    async () => {
      const response = await restFactory.get<PlanPermission>(
        `/dj/api/v1/chargebee_subscription_multiuser/get_plan_permissions/`
      );
      return response;
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const subscriptions = ownedSubscriptions?.results || [];
  const planPermissions = ownedPlanPermissions || {};

  const handleRefetch = () => {
    ownedSubscriptionsRefetch();
    ownedPlanPermissionsRefetch();
  };

  const { mutate: updateLicenses, isLoading: isUpdateLicensesLoading } =
    useMutation(
      async (data: SubscriptionsChangesField) => {
        const cleanedData = {
          subscriptions: data.licensesChanges
            .filter((lic) => lic.selected)
            .map((lic) => lic.id),
          user_id: member.id,
        };
        return await restFactory.patch(
          `/dj/api/v1/chargebee_subscription_multiuser/list_update_subscriptions/`,
          cleanedData
        );
      },
      {
        onSuccess: () => {
          onClose?.();
          toast.success('Licenses updated.');
        },
        onError: () => {
          handleRefetch();
          toast.error('Error updating licenses.');
        },
        onSettled: () => {
          queryClient.invalidateQueries(infiniteStrapiUsersKeys.all);
        },
      }
    );

  const handleOnSubmit = (data: SubscriptionsChangesField) => {
    updateLicenses(data);
  };

  const render = () => {
    if (ownedSubscriptionsIsError || ownedPlanPermissionsIsError) {
      return <div className="p-5">Try again later.</div>;
    }
    if (!ownedSubscriptions || !ownedPlanPermissions) {
      return (
        <div className="p-5">
          <div className="loadingIndicatorContainer">
            <Spinner role="progressbar" animation="border" />
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={classnames('border-primary pl-2 mt-5')}>
          <LicensesForm
            member={member}
            planPermissions={planPermissions}
            subscriptions={subscriptions}
            readOnly={readOnly}
          />
        </div>
      </>
    );
  };

  return (
    <FormModal
      title={readOnly ? 'Licenses' : getTitle(member)}
      shouldResetForm={false}
      onClose={() => onClose?.()}
      formContext={formContext}
      className={classnames(styles.viewModals)}
      onSubmit={handleOnSubmit}
      isLoading={isUpdateLicensesLoading}
      showFooter={!readOnly}
      showCloseButton={readOnly}
    >
      <>{render()}</>
    </FormModal>
  );
};

export default ReviewLicensesModal;
